import { useState, useEffect } from "react";
import axios from "axios";
import { Search, ArrowLeft, Award, Zap, ExternalLink, Users, Building2, Calendar } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { PoliticianGridCard } from "../components/politician/PoliticianGridCard";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHAMBERS = [
  { id: "all", label: "All Members" },
  { id: "House of Representatives", label: "House of Reps" },
  { id: "Senate", label: "Senate" },
];

const PARTY_FILTERS = [
  { id: "all", label: "All Parties" },
  { id: "alp", label: "Labor" },
  { id: "liberal", label: "Liberal" },
  { id: "greens", label: "Greens" },
  { id: "teal", label: "Teal" },
  { id: "nationals", label: "Nationals" },
  { id: "independent", label: "Independent" },
  { id: "one-nation", label: "One Nation" },
  { id: "jln", label: "JLN" },
];

export default function PoliticiansPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [politicians, setPoliticians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [chamber, setChamber] = useState("all");
  const [partyFilter, setPartyFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [divisions, setDivisions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(true);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "#334155" : "#000";

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (chamber !== "all") params.append("chamber", chamber);
    if (partyFilter !== "all") params.append("party_id", partyFilter);
    if (levelFilter !== "all") params.append("level", levelFilter);
    if (search) params.append("search", search);
    axios
      .get(`${API}/politicians?${params}`)
      .then((r) => setPoliticians(r.data))
      .catch(() => setPoliticians([]))
      .finally(() => setLoading(false));
  }, [chamber, partyFilter, levelFilter, search]);

  useEffect(() => {
    axios
      .get(`${API}/tvfy/recent-divisions`)
      .then((r) => setDivisions(r.data || []))
      .catch(() => setDivisions([]))
      .finally(() => setDivisionsLoading(false));
  }, []);

  const houseCount = politicians.filter((p) => p.chamber === "House of Representatives").length;
  const senateCount = politicians.filter((p) => p.chamber === "Senate").length;
  const partySet = new Set(politicians.map((p) => p.party_name));

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="politicians-page">
      {/* Hero */}
      <div
        className="py-12 px-4 border-b-2 border-black"
        style={{ backgroundColor: "#0F172A" }}
      >
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="px-3 py-1 text-xs font-black border-2 border-black"
                  style={{ backgroundColor: "#FFC82E", color: "#000" }}
                >
                  ACCOUNTABILITY
                </div>
              </div>
              <h1
                className="text-4xl sm:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                They Vote For You
              </h1>
              <p className="text-base max-w-xl" style={{ color: "#94A3B8" }}>
                Track how Federal and State politicians actually vote on the issues that matter most to Australians.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => navigate("/politicians/compare")}
                className="flex items-center gap-1.5 px-4 py-2 border-2 border-white text-sm font-black text-white hover:bg-white hover:text-black transition-colors"
                data-testid="nav-compare-btn"
              >
                Compare
              </button>
              <button
                onClick={() => navigate("/policies")}
                className="flex items-center gap-1.5 px-4 py-2 border-2 border-white text-sm font-black text-white hover:bg-white hover:text-black transition-colors"
                data-testid="nav-policies-btn"
              >
                Policy Votes
              </button>
              <button
                onClick={() => navigate("/promises")}
                className="flex items-center gap-1.5 px-4 py-2 border-2 border-black text-sm font-black hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0 rgba(255,255,255,0.3)" }}
                data-testid="nav-promises-btn"
              >
                Promises Tracker
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { label: "Federal Politicians", value: politicians.length || 20, icon: Users },
              { label: "House Members", value: houseCount || "—", icon: Building2 },
              { label: "Senators", value: senateCount || "—", icon: Award },
              { label: "Parties Represented", value: partySet.size || 8, icon: null },
            ].map(({ label, value, icon: Icon }) => (
              <div
                key={label}
                className="border-2 border-black px-4 py-2 flex items-center gap-2"
                style={{ backgroundColor: "rgba(255,200,46,0.1)" }}
              >
                {Icon && <Icon className="w-4 h-4 text-yellow-400" />}
                <span className="text-xl font-black text-white">{value}</span>
                <span className="text-xs font-bold" style={{ color: "#94A3B8" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search + Filters */}
        <div className="flex flex-col gap-4 mb-8">
          {/* Search bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textSec }} />
            <input
              type="text"
              placeholder="Search politicians by name, role or party…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border-2 border-black text-sm font-medium outline-none focus:ring-0"
              style={{ backgroundColor: surface, color: text }}
              data-testid="politician-search-input"
            />
          </div>

          {/* Level filter */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { id: "all", label: "All Levels" },
              { id: "federal", label: "Federal" },
              { id: "state", label: "State" },
            ].map((l) => (
              <button
                key={l.id}
                onClick={() => setLevelFilter(l.id)}
                data-testid={`level-filter-${l.id}`}
                className="px-3 py-1.5 text-xs font-black border-2 border-black transition-all"
                style={{
                  backgroundColor: levelFilter === l.id ? "#0F172A" : surface,
                  color: levelFilter === l.id ? "#FFC82E" : text,
                  boxShadow: levelFilter === l.id ? "2px 2px 0 #000" : "none",
                }}
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Chamber filter */}
          <div className="flex flex-wrap gap-1.5">
            {CHAMBERS.map((c) => (
              <button
                key={c.id}
                onClick={() => setChamber(c.id)}
                data-testid={`chamber-filter-${c.id}`}
                className="px-3 py-1.5 text-xs font-black border-2 border-black transition-all"
                style={{
                  backgroundColor: chamber === c.id ? "#FFC82E" : surface,
                  color: chamber === c.id ? "#000" : text,
                  boxShadow: chamber === c.id ? "2px 2px 0 #000" : "none",
                }}
              >
                {c.label}
              </button>
            ))}
            <div className="w-px bg-black mx-1 self-stretch" />
            {PARTY_FILTERS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPartyFilter(p.id)}
                data-testid={`party-filter-${p.id}`}
                className="px-3 py-1.5 text-xs font-bold border-2 border-black transition-all"
                style={{
                  backgroundColor: partyFilter === p.id ? "#FFC82E" : surface,
                  color: partyFilter === p.id ? "#000" : text,
                  boxShadow: partyFilter === p.id ? "2px 2px 0 #000" : "none",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="border-2 border-black h-44 animate-pulse"
                style={{ backgroundColor: surface }}
              />
            ))}
          </div>
        ) : politicians.length === 0 ? (
          <div
            className="text-center py-20 border-2 border-black"
            style={{ backgroundColor: surface }}
            data-testid="no-politicians-found"
          >
            <p className="text-lg font-black mb-1" style={{ color: text }}>No politicians found</p>
            <p className="text-sm" style={{ color: textSec }}>Try adjusting your filters or search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="politicians-grid">
            {politicians.map((p) => (
              <PoliticianGridCard
                key={p.id}
                p={p}
                surface={surface}
                text={text}
                textSec={textSec}
                border={border}
              />
            ))}
          </div>
        )}
        {/* Live Parliamentary Divisions */}
        <div className="mt-12" data-testid="divisions-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: "#FFC82E" }} />
              <h2
                className="text-xl font-black"
                style={{ color: text, fontFamily: "Outfit, sans-serif" }}
              >
                Recent Parliamentary Divisions
              </h2>
            </div>
            <span
              className="text-xs font-black px-2 py-0.5 border border-black"
              style={{ backgroundColor: "#FFC82E", color: "#000" }}
            >
              LIVE
            </span>
            <a
              href="https://theyvoteforyou.org.au"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs font-bold flex items-center gap-1 hover:opacity-75"
              style={{ color: textSec }}
            >
              theyvoteforyou.org.au <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {divisionsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border-2 border-black h-24 animate-pulse" style={{ backgroundColor: surface }} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {divisions.slice(0, 12).map((div) => (
                <div
                  key={div.id}
                  className="border-2 border-black p-3"
                  style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
                  data-testid={`division-card-${div.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span
                      className="text-xs font-black px-1.5 py-0.5 border border-black"
                      style={{ backgroundColor: div.house === "representatives" ? "#2E5948" : "#1B4F9E", color: "#fff" }}
                    >
                      {div.house === "representatives" ? "House" : "Senate"}
                    </span>
                    <div className="flex items-center gap-1 text-xs" style={{ color: textSec }}>
                      <Calendar className="w-3 h-3" />
                      {div.date}
                    </div>
                  </div>
                  <p
                    className="text-sm font-black capitalize leading-snug"
                    style={{ color: text, fontFamily: "Outfit, sans-serif" }}
                  >
                    {div.name}
                  </p>
                  {div.summary && (
                    <p className="text-xs mt-1.5 line-clamp-2" style={{ color: textSec }}>
                      {div.summary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          {!divisionsLoading && divisions.length === 0 && (
            <p className="text-sm" style={{ color: textSec }}>No live division data available right now.</p>
          )}
        </div>
      </div>
    </div>
  );
}
